import {
  Breadcrumb,
  BreadcrumbIcon,
  BreadcrumbLink,
  BreadcrumbPage,
  Button,
  TabMenu,
  TabMenuList,
  TabMenuTrigger,
} from '@ads/components-react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@/shared/ui/organisms/PageHeader';
import { useGradeConfig } from '../../../hooks/useGradeConfig';
import { useGradeConfigTabs } from '../../../hooks/useGradeConfigTabs';
import { BulkLinkModal } from '../../organisms/BulkLinkModal';
import { ClassContextBar } from '../../organisms/ClassContextBar';
import { GradeConfigEmptyState } from '../../organisms/GradeConfigEmptyState';
import { GradeConfigSkeleton } from '../../organisms/GradeConfigSkeleton';
import { LinkedDisciplinesTable } from '../../organisms/LinkedDisciplinesTable';
import { ModulesCard } from '../../organisms/ModulesCard';
import { StudentDeclarationPanel } from '../../organisms/StudentDeclarationPanel';

/** Props do template: estado de carga da turma selecionada (vindo do boundary de dados). */
export interface GradeConfigTemplateProps {
  /** Carregando a configuração da turma selecionada. */
  readonly isLoading?: boolean;
  /** Falha ao carregar a configuração da turma. */
  readonly isError?: boolean;
  /** Refaz a busca da configuração da turma. */
  readonly onRetry?: () => void;
}

/**
 * Template da tela **Configuração de Nota e Frequência** — conteúdo do `<main>` (sem
 * header/navbar próprios: vêm do `AppShell`). Compõe PageHeader + barra de contexto (sempre
 * visível) e, conforme o estado da seleção/carga: empty state (nenhuma turma), skeleton
 * (carregando), estado de erro (falha) ou a configuração completa da turma ativa.
 */
export function GradeConfigTemplate({ isLoading, isError, onRetry }: GradeConfigTemplateProps) {
  const { t } = useTranslation('gradeAttendanceConfig');
  const { tab, isConfig, setTab } = useGradeConfigTabs();
  const { classGroup, hasSelection } = useGradeConfig();

  return (
    <div className="flex flex-col gap-xxs">
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        breadcrumb={
          <Breadcrumb>
            <BreadcrumbLink>
              <Link to="/">
                <BreadcrumbIcon />
              </Link>
            </BreadcrumbLink>

            <BreadcrumbLink>
              <Link to={'/exams' as never} search={{} as never}>
                {t('breadcrumb.section')}
              </Link>
            </BreadcrumbLink>
            <BreadcrumbPage>{t('breadcrumb.current')}</BreadcrumbPage>
          </Breadcrumb>
        }
      />

      <ClassContextBar />

      {!hasSelection ? (
        <GradeConfigEmptyState />
      ) : isLoading ? (
        <GradeConfigSkeleton />
      ) : isError || !classGroup ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-xxs rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface px-xxs py-xl">
          <h2 className="text-ctx-content-title ts-heading-xs">{t('loadError.title')}</h2>
          <p className="max-w-[26rem] text-center text-ctx-content-base ts-paragraph-xs">
            {t('loadError.description')}
          </p>
          {onRetry ? (
            <Button hierarchy="secondary" size="md" leadingIcon={ArrowPathIcon} onClick={onRetry}>
              {t('loadError.retry')}
            </Button>
          ) : null}
        </div>
      ) : (
        <>
          <TabMenu value={tab} onValueChange={v => setTab(v as 'config' | 'resultado')}>
            <TabMenuList>
              <TabMenuTrigger value="config">{t('tabs.config')}</TabMenuTrigger>
              <TabMenuTrigger value="resultado">{t('tabs.resultado')}</TabMenuTrigger>
            </TabMenuList>
          </TabMenu>

          {isConfig ? (
            <div className="flex flex-col gap-xxs">
              <ModulesCard />
              <LinkedDisciplinesTable />
            </div>
          ) : (
            <div className="flex flex-col gap-xxs">
              <StudentDeclarationPanel />
            </div>
          )}

          <BulkLinkModal />
        </>
      )}
    </div>
  );
}
